import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { Autocomplete, FormControl, TextField } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import GroupIcon from '@mui/icons-material/Group';
import WorkIcon from '@mui/icons-material/Work';

const optionIcons = {
  Organisation: (
    <WorkIcon
      fontSize="small"
      sx={{ color: '#404040' }}
    />
  ),
  Group: (
    <GroupsIcon
      fontSize="small"
      sx={{ color: '#404040' }}
    />
  ),
  SubGroup: (
    <GroupIcon
      fontSize="small"
      sx={{ color: '#404040' }}
    />
  ),
  default: (
    <PersonIcon
      fontSize="small"
      sx={{ color: '#404040' }}
    />
  ),
};

const getOptionIcon = (type) => optionIcons[type] || optionIcons.default;

export const RecipientField = ({
  id = 'recipientField',
  name = 'recipientField',
  label = '',
  size = 'small',
  optionsData = [],
  backendSearch = () => {},
  loading = false,
  handleShare = () => {},
}) => {
  const [inputValue, setInputValue] = useState('');
  const [value, setValue] = useState(null);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);

  // Memoize debounce function so it remains stable across renders
  const debouncedSearch = useMemo(() => debounce(backendSearch, 1000), [backendSearch]);

  useEffect(() => {
    setOptions(optionsData);
  }, [optionsData]);

  useEffect(() => {
    return () => {
      // Cleanup debounce function on unmount to prevent memory leaks
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <FormControl
      fullWidth
      size={size}
    >
      <Autocomplete
        id={id}
        name={name}
        value={value}
        onChange={(event, newValue) => {
          setValue(null);
          setInputValue('');
          handleShare(newValue);
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
          // Prevent triggering search if input is empty
          if (!newInputValue.trim() || event?.type === 'click') return;
          debouncedSearch(newInputValue);
        }}
        sx={{ width: 300 }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        isOptionEqualToValue={(option, value) => option?.name === value?.name}
        getOptionLabel={(option) => option?.name || ''}
        options={options}
        loading={loading}
        renderOption={(props, option) => (
          <li
            {...props}
            key={`${props.key}=${option.name}`}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {getOptionIcon(option.type)}
            <span
              style={{
                fontSize: '14px',
                flex: 1,
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.2,
              }}
            >
              {option.name}
            </span>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size={size}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && (
                    <CircularProgress
                      color="inherit"
                      size={20}
                    />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        disableClearable
      />
    </FormControl>
  );
};

RecipientField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.string,
  optionsData: PropTypes.array,
  backendSearch: PropTypes.func,
  loading: PropTypes.bool,
  handleShare: PropTypes.func,
};
